/** The wizard's write layer: wizard values in, existing service calls out.
 *
 *  Kept separate from the components so each conversion (hours→minutes, "" →null, a dayjs date
 *  →epoch seconds) is unit-testable without rendering, and so the steps never import a service.
 *
 *  Every function throws on failure and lets the page map the error through `getErrorMessage`.
 *  A function that can half-succeed returns a `warning` instead of throwing — see `saveWorkout`. */

import moment from "moment";
import type { AdminNewTemplate, AdminWeightTableWeek } from "../../../api/generated";
import {
  createTrainee,
  getClientWeightTable,
  updateTrainee,
  updateWeightTable,
} from "../../../services/TraineeService";
import { changeProgram } from "../../../services/PlanSerice";
import { saveMenu } from "../../../services/NutritionV2Service";
import { createCheckIn } from "../../../services/CheckInService";
import { createScheduledCheckIn } from "../../../services/ScheduledCheckInService";
import { assignTemplateToTrainee } from "../../../services/FormTemplateService";
import { createMenu } from "../../nutrition/v2/model/nutritionV2Model";
import { calendarDayToUtcMidnightSeconds, todayLocal } from "../../../utils/epochTime";
import { getErrorMessage } from "../../../enums/ErrorMessages";
import type {
  CheckInsValues,
  GoalsValues,
  IdentityValues,
  NutritionValues,
  StepId,
  StepStatus,
  WorkoutValues,
} from "./wizardReducer";

export interface SaveResult {
  /** The step persisted, but something secondary did not. Shown as a warning, not an error. */
  warning?: string;
}

/** `""` means "the coach left it blank", which the server reads as "clear it" — so send null. */
const toNumber = (value: string): number | null => {
  const trimmed = value.trim();
  if (trimmed === "") return null;
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : null;
};

const hoursToMinutes = (hours: string): number | null => {
  const parsed = toNumber(hours);
  return parsed === null ? null : Math.round(parsed * 60);
};

/** A dayjs value from `@mui/x-date-pickers` → the epoch SECONDS every date field here uses. */
const toEpochSeconds = (value: unknown): number | null => {
  const date = (value as { $d?: Date } | null)?.$d;
  if (!date || Number.isNaN(date.getTime())) return null;
  return Math.floor(date.getTime() / 1000);
};

/** Israeli local form (`05…`) → E.164, matching what the previous create dialog sent. */
const toE164 = (input: string): string => {
  if (!input) return "";
  let digits = input.replace(/\D/g, "");
  if (digits.startsWith("972")) return `+${digits}`;
  if (digits.startsWith("0")) digits = digits.slice(1);
  return `+972${digits}`;
};

// ---------------------------------------------------------------------------------------------
// Identity
// ---------------------------------------------------------------------------------------------

/**
 * Creates the trainee and returns its id.
 *
 * No `companyId`: `CreateTraineeRequest` has no such field and `AdminTraineeController.create`
 * passes `null` to the service, which derives the company from the principal. (That is also why
 * there is no company picker — a SYSTEM_ADMIN cannot create a trainee through this endpoint at
 * all, since the service rejects a null company for that role.)
 *
 * `templateId` is deliberately NOT sent even though the DTO accepts it. Passing it here would
 * assign a program inside the create transaction, but it hard-codes `ProgramSettings` to null,
 * discarding the four toggles the workout step offers. The workout step assigns it instead; see
 * `saveWorkout`.
 *
 * This call is also what sends the welcome email, which is why it runs at the END of the create
 * wizard rather than the start — see `commitNewClient`.
 */
export const createTraineeFromIdentity = async (values: IdentityValues): Promise<number> => {
  const isExisting = values.source === "existing" && values.existingUser != null;

  const body = {
    userId: isExisting ? values.existingUser?.id : null,
    name: values.name.trim(),
    email: values.email.trim(),
    // An existing user already has one; sending null leaves it untouched.
    password: isExisting ? null : values.password,
    phoneNumber: toE164(values.phoneNumber),
    gender: values.gender || null,
    dateOfBirth: toEpochSeconds(values.dateOfBirth),
    expirationDate: toEpochSeconds(values.expirationDate),
    bodyWeight: toNumber(values.bodyWeight),
    height: toNumber(values.height),
    trainerId: toNumber(values.trainerId),
    sendWelcomeEmail: isExisting ? false : values.sendWelcomeEmail,
  };

  const { data } = await createTrainee(body, null);
  const id = data?.id;
  if (typeof id !== "number") throw new Error("createTrainee returned no id");
  return id;
};

// ---------------------------------------------------------------------------------------------
// Goals
// ---------------------------------------------------------------------------------------------

/**
 * The week row the goals step must edit in place.
 *
 * Fetches a three-week window and picks the latest week that has already started. The GET
 * synthesizes missing weeks, so this works on a trainee created seconds ago.
 */
export const loadCurrentWeek = async (traineeId: number): Promise<AdminWeightTableWeek | null> => {
  const { data } = await getClientWeightTable(
    traineeId,
    moment().subtract(1, "week"),
    moment().add(1, "week"),
  );
  const today = calendarDayToUtcMidnightSeconds(todayLocal());
  const started = (data?.weeks ?? []).filter(
    (week: AdminWeightTableWeek) => week.weekDate != null && week.weekDate <= today,
  );
  if (started.length === 0) return null;
  return started.reduce((latest: AdminWeightTableWeek, week: AdminWeightTableWeek) =>
    (week.weekDate ?? 0) > (latest.weekDate ?? 0) ? week : latest,
  );
};

/** Maps a loaded week back onto the step's inputs, so blanks mean "cleared", not "unknown". */
export const goalsFromWeek = (week: AdminWeightTableWeek): Partial<GoalsValues> => ({
  goal: week.goal ?? "",
  weeklyRange: week.weeklyRange ?? "",
  stepsGoal: week.stepsGoal != null ? String(week.stepsGoal) : "",
  sleepGoalHours: week.sleepGoalMinutes != null ? String(week.sleepGoalMinutes / 60) : "",
  caloriesGoal: week.caloriesGoal != null ? String(week.caloriesGoal) : "",
  weekRow: week,
});

/**
 * Writes the weekly goals WITHOUT touching anything else on the week.
 *
 * The whole loaded row is spread back because the endpoint is a week replace — see the long note
 * on `GoalsValues.weekRow` for what a minimal body destroys. `weekDate` comes from the row, never
 * computed locally.
 */
export const saveGoals = async (traineeId: number, values: GoalsValues): Promise<SaveResult> => {
  if (!values.weekRow?.weekDate) {
    // Refusing beats guessing an anchor: a wrong `weekDate` creates a duplicate week row and
    // carries all seven daily-metric writes to the wrong days.
    throw new Error("missing week row — cannot save goals without the server's weekDate");
  }

  await updateWeightTable(traineeId, {
    ...values.weekRow,
    goal: values.goal || null,
    weeklyRange: values.weeklyRange || null,
    stepsGoal: toNumber(values.stepsGoal),
    sleepGoalMinutes: hoursToMinutes(values.sleepGoalHours),
    caloriesGoal: toNumber(values.caloriesGoal),
  });

  return {};
};

// ---------------------------------------------------------------------------------------------
// Nutrition
// ---------------------------------------------------------------------------------------------

/**
 * Applies a V2 menu template to the trainee in one call.
 *
 * The payload carries no meals on purpose: the server answers a create carrying a `templateId`
 * with the TEMPLATE's meals and ignores the ones in the body (the behaviour `MenuBuilderV2Page`
 * documents from the "it resets my foods" bug). Here that is exactly what we want.
 */
export const saveNutrition = async (
  traineeId: number,
  values: NutritionValues,
  templateName?: string,
): Promise<SaveResult> => {
  if (!values.templateId) throw new Error("no menu template selected");

  const menu = createMenu(String(traineeId), { name: templateName || "תפריט חדש" });
  await saveMenu(menu, values.templateId);
  return {};
};

// ---------------------------------------------------------------------------------------------
// Workout
// ---------------------------------------------------------------------------------------------

/**
 * Assigns the workout template, then back-fills the three trainee attributes the template implies.
 *
 * `createTrainee` does that back-fill when given a `templateId`; `changeProgram` does not, and the
 * omission bites later — `ChnageProgramDialog` filters its template list by exactly
 * `numOfWorkouts` / `experienceLevel` / `equipmentType`, so a trainee missing them gets a useless
 * picker the next time a coach changes their program. `updateTrainee` is explicitly PATCH-semantic
 * (every field is `Optional.ofNullable(...).ifPresent(...)`), so a three-field body is safe.
 *
 * The program is what the coach asked for, so a failed back-fill is reported as a warning: saying
 * "failed" about a program that is now assigned would send them to re-assign it.
 */
export const saveWorkout = async (
  traineeId: number,
  values: WorkoutValues,
  template: AdminNewTemplate | null,
): Promise<SaveResult> => {
  if (!values.templateId) throw new Error("no workout template selected");

  await changeProgram(traineeId, values.templateId, values.includeRmWeek, {
    automatic: values.automaticProgression,
    useProgressionAlgorithm: values.useProgressionAlgorithm,
    useRir: values.showRir,
  });

  if (!template) return {};

  try {
    await updateTrainee({
      id: traineeId,
      numOfDaysTraining: template.numOfWorkouts,
      experienceLevel: template.experienceLevel,
      equipmentType: template.equipmentType,
    });
  } catch {
    return { warning: "התוכנית הוקצתה, אך פרטי האימון של המתאמן לא עודכנו" };
  }

  return {};
};

// ---------------------------------------------------------------------------------------------
// Check-ins and forms
// ---------------------------------------------------------------------------------------------

/**
 * One check-in request plus a form assignment per selected template.
 *
 * The form assignments are independent writes, so they are settled rather than chained — four
 * forms where the second is un-assignable should still assign the other three, and say so.
 */
export const saveCheckInsAndForms = async (
  traineeId: number,
  values: CheckInsValues,
  clientName?: string,
): Promise<SaveResult> => {
  if (values.itemTypes.length > 0) {
    const items = values.itemTypes.map(type => ({ type, required: true }));

    if (values.mode === "recurring") {
      // A schedule of this client's own, created in one POST — see the note on `CheckInsValues.mode`
      // for why the wizard never edits an existing shared schedule instead.
      await createScheduledCheckIn({
        name: clientName ? `צ'ק-אין — ${clientName}` : "צ'ק-אין",
        frequency: values.frequency,
        scheduleDays: values.frequency === "WEEKLY" ? values.scheduleDays : [],
        daysOfMonth: values.frequency === "MONTHLY" ? values.daysOfMonth : [],
        intervalWeeks: values.frequency === "WEEKLY" ? values.intervalWeeks : null,
        intervalMonths: values.frequency === "MONTHLY" ? values.intervalMonths : null,
        scheduleTime: values.scheduleTime,
        sendToAll: false,
        trainerId: null,
        traineeIds: [traineeId],
        items,
      });
    } else {
      await createCheckIn({ traineeIds: [traineeId], sendToAll: false, items });
    }
  }

  if (values.formTemplateIds.length === 0) return {};

  const results = await Promise.allSettled(
    values.formTemplateIds.map(templateId =>
      assignTemplateToTrainee(templateId, traineeId, null, false),
    ),
  );
  const failed = results.filter(result => result.status === "rejected").length;
  if (failed === 0) return {};
  if (failed === values.formTemplateIds.length) throw new Error("every form assignment failed");

  return {
    warning: `הוקצו ${values.formTemplateIds.length - failed} מתוך ${values.formTemplateIds.length} טפסים`,
  };
};

// ---------------------------------------------------------------------------------------------
// The create-mode commit
// ---------------------------------------------------------------------------------------------

/** What `commitNewClient` reports back per step, for the summary and the rail. */
export interface CommitOutcome {
  traineeId: number;
  results: Partial<Record<StepId, { status: StepStatus; error?: string }>>;
}

/** A step is worth committing when the coach actually filled it in. */
const wasFilled = (status: StepStatus) => status === "ready" || status === "dirty";

/**
 * Creates the client and applies everything the coach chose, in one go, when they press סיום.
 *
 * WHY THE CREATE IS LAST. `POST /admin/trainee` is what sends the welcome email — the server
 * builds it from the plain-text password and then hashes and forgets it, and no resend endpoint
 * exists. Creating up front therefore emailed the client their login the moment the coach started,
 * minutes before the menu and program existed, so they could log in to an empty app. Holding the
 * whole wizard client-side and creating here means the email goes out only once the coach has
 * finished deciding.
 *
 * ONCE THE CLIENT EXISTS, NOTHING ELSE MAY THROW. The create is the only fatal step; after it, a
 * failed assignment is recorded against its step and the rest still run. Aborting half way would
 * leave a client who exists but whose remaining steps were never attempted, with no record of
 * which — the coach would have to work it out by hand.
 */
export const commitNewClient = async (
  steps: {
    identity: IdentityValues;
    goals: { status: StepStatus; values: GoalsValues };
    nutrition: { status: StepStatus; values: NutritionValues };
    workout: { status: StepStatus; values: WorkoutValues };
    checkins: { status: StepStatus; values: CheckInsValues };
  },
  lookups: {
    menuTemplateName?: (id: string) => string | undefined;
    workoutTemplate?: (id: number) => AdminNewTemplate | null;
    /** Names the recurring check-in schedule, so it is identifiable on the check-ins screen. */
    clientName?: string;
  } = {},
): Promise<CommitOutcome> => {
  // Fatal on purpose: with no client, there is nothing to attach anything to.
  const traineeId = await createTraineeFromIdentity(steps.identity);

  const results: CommitOutcome["results"] = { identity: { status: "saved" } };

  const attempt = async (step: StepId, write: () => Promise<SaveResult>) => {
    try {
      const { warning } = await write();
      results[step] = { status: "saved", error: warning };
    } catch (error) {
      const code = (error as { response?: { data?: { errorCode?: number } } })?.response?.data
        ?.errorCode;
      results[step] = { status: "failed", error: getErrorMessage(code) };
    }
  };

  if (wasFilled(steps.goals.status)) {
    // The weekly row has to be read first even here: `createTrainee` has just written an initial
    // weigh-in from `bodyWeight`, and the endpoint replaces the whole week.
    await attempt("goals", async () => {
      const week = await loadCurrentWeek(traineeId);
      if (!week) throw new Error("no week row to write goals onto");
      return saveGoals(traineeId, { ...steps.goals.values, weekRow: week });
    });
  }

  if (wasFilled(steps.nutrition.status) && steps.nutrition.values.templateId) {
    const name = lookups.menuTemplateName?.(steps.nutrition.values.templateId);
    await attempt("nutrition", () => saveNutrition(traineeId, steps.nutrition.values, name));
  }

  if (wasFilled(steps.workout.status) && steps.workout.values.templateId != null) {
    const template = lookups.workoutTemplate?.(steps.workout.values.templateId) ?? null;
    await attempt("workout", () => saveWorkout(traineeId, steps.workout.values, template));
  }

  if (wasFilled(steps.checkins.status)) {
    await attempt("checkins", () =>
      saveCheckInsAndForms(traineeId, steps.checkins.values, lookups.clientName),
    );
  }

  return { traineeId, results };
};
